import {
  Table,
  Dropdown,
  Pill,
  Title,
  BackButton,
  ScrollView,
  FilterIndicator,
} from '@chillibean/component-library/legacy';
import pluralize from 'pluralize';
import { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import prepareAssetRowData from '../../../__util__/Table/assetHelpers';
import ReportingSpinner from '../../../__util__/Table/ReportingSpinner';
import AssetReporting from '../../../api/reporting/AssetReporting';
import useHttpRequest from '../../../CustomHooks/useHttpRequest/useHttpRequest';
import LogAndDisplayError from '../../../LogAndDisplayError/LogAndDisplayError';
import useNavigator from '../hooks/useNavigator';
import AssetsFilter from '../presentational/AssetsFilter/AssetsFilter';
import ExportModal from '../presentational/ExportModal';
import { selectInternalResult } from '../redux/internal.slice';

function AssetsReportTemplateReport() {
  // state
  const [tableHeaders, setTableHeaders] = useState([]);
  const [tableDataRows, setTableDataRows] = useState([]);
  const [tableDataReady, setTableDataReady] = useState(false);
  const [tableEmpty, setTableEmpty] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [searchableAttributes, setSearchableAttributes] = useState([]);
  const [trialName, setTrialName] = useState(null);
  const [filtersNumber, setFiltersNumber] = useState(0);
  const [assetFilterKey, setAssetFilterKey] = useState(true);
  const [filterParams, setFilterParams] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // set variables
  const { accountId, sitesEnabled } = useSelector(selectInternalResult) || { sitesEnabled: false };
  const {
    state: { status, data, error },
    fetchRequest,
  } = useHttpRequest();
  const moreDataRequester = useHttpRequest();
  const infoFieldSearchRequester = useHttpRequest();
  // handlers
  const { goBack } = useNavigator();

  function determineLoading() {
    return status !== 'resolved';
  }

  function toggleExportModal() {
    setExportModalVisible(!exportModalVisible);
  }

  function tableHasMoreRecords() {
    if (data && tableDataReady) {
      const { hits, per_page: perPage } = data;
      return hits > perPage && tableDataRows.length < hits;
    }
    return false;
  }

  const toggleShowFilterModal = () => {
    setShowFilterModal(!showFilterModal);
  };

  const fetchData = () => {
    const nextPage = currentPage + 1;
    moreDataRequester.fetchRequest(AssetReporting.search(filterParams, nextPage));
    setCurrentPage(nextPage);
  };

  useEffect(() => {
    if (!accountId) return;

    if (status === 'idle') {
      fetchRequest(AssetReporting.search());
    }

    if (status === 'resolved') {
      setTrialName(data.trial_name);
      if (data.results.length === 0) {
        setTableEmpty(true);
      } else {
        const preparedTableData = prepareAssetRowData(data.results, data.info_fields, { sitesEnabled });
        setTableHeaders([...preparedTableData.headersReadyForTable]);
        setTableDataRows([...preparedTableData.dataReadyForTable]);
        setTableDataReady(true);
        setTableEmpty(false);
      }
    }
  }, [accountId, status]);

  useEffect(() => {
    if (moreDataRequester.state.status === 'resolved' && data.results.length !== 0) {
      const processedData = prepareAssetRowData(
        moreDataRequester.state.data.results,
        moreDataRequester.state.data.info_fields,
        {
          sitesEnabled,
        },
      ).dataReadyForTable;
      setTableDataRows(tableDataRows.concat(processedData));
    }
  }, [moreDataRequester.state.status]);

  useEffect(() => {
    if (infoFieldSearchRequester.state.status === 'idle' && status === 'resolved') {
      infoFieldSearchRequester.fetchRequest(AssetReporting.infoFields());
    }
  }, [infoFieldSearchRequester.state.status, status]);

  useEffect(() => {
    if (infoFieldSearchRequester.state.status === 'resolved') {
      const attributes = infoFieldSearchRequester.state.data;

      setSearchableAttributes(attributes);
    }
  }, [infoFieldSearchRequester.state.data]);

  if (status === 'error') {
    return <LogAndDisplayError error={error} />;
  }

  const applyFilters = (params) => {
    fetchRequest(AssetReporting.search(params));
    setFilterParams(params);
    setCurrentPage(1);
    setTableDataReady(false);
  };

  const clearFilters = () => {
    fetchRequest(AssetReporting.search());
    setFiltersNumber(0);
    setAssetFilterKey(!assetFilterKey);
    setFilterParams({});
    setCurrentPage(1);
  };

  const tablePlaceholder = () => {
    // if there are no filters applied and there ane no assets displayed, then it means that the account has no assets
    const emptyAccount = filtersNumber === 0;

    if (emptyAccount) {
      return (
        <EmptyTableContainer>
          <InfoEmptyContainer>
            <ClearFilterText>There are no assets to show yet.</ClearFilterText>
          </InfoEmptyContainer>
        </EmptyTableContainer>
      );
    }

    return (
      <EmptyTableContainer>
        <Table
          headers={tableHeaders}
          tableData={[]}
          loading={false}
          hasMore={false}
          spinnerComponent={<ReportingSpinner />}
        />
        <InfoContainer>
          <ClearFilterText>No results found for these filters. Adjust or clear filters to try again.</ClearFilterText>
          <ClearFilterButton type="submit" onClick={clearFilters}>
            Clear all filters
          </ClearFilterButton>
        </InfoContainer>
      </EmptyTableContainer>
    );
  };

  return (
    <div className="single_column_container">
      <div className="header-wrapper-formReport">
        <div className="left-items">
          <BackButton onClickCallback={goBack} loading={determineLoading()} />
          <Title heading={`${trialName}'s Asset Report`} subheading="" loading={determineLoading()} />
          {tableEmpty === false && (
            <Pill
              dataCount={String(data?.hits) || '0'}
              dataContext={pluralize('Asset', data?.hits)}
              loading={determineLoading()}
            />
          )}
        </div>
        <div className="right-items">
          {!determineLoading() && (
            <>
              <FilterContainer>
                <FilterIndicator handleClick={toggleShowFilterModal} value={filtersNumber} />
              </FilterContainer>
              <Dropdown
                options={[
                  {
                    label: 'Export data',
                    onSelect: toggleExportModal,
                  },
                ]}
                icon="kebab"
              />
            </>
          )}
        </div>
      </div>
      <div className="scroll-table-wrapper">
        {tableEmpty ? (
          tablePlaceholder()
        ) : (
          <InfiniteScroll
            dataLength={tableDataRows.length}
            next={fetchData}
            scrollableTarget="scrollableDiv"
            hasMore={tableHasMoreRecords()}
            style={{ height: '100%' }}
          >
            <ScrollView idName="scrollableDiv">
              <Table
                headers={tableHeaders}
                tableData={tableDataRows}
                loading={!tableDataReady}
                hasMore={tableHasMoreRecords()}
                spinnerComponent={<ReportingSpinner />}
              />
            </ScrollView>
          </InfiniteScroll>
        )}
      </div>
      <AssetsFilter
        key={assetFilterKey}
        showModal={showFilterModal}
        setShowFilterModal={setShowFilterModal}
        searchableAttributes={searchableAttributes}
        handleToggle={toggleShowFilterModal}
        handleApply={applyFilters}
        setFiltersNumber={setFiltersNumber}
      />
      <ExportModal
        showModal={exportModalVisible}
        setShowModal={setExportModalVisible}
        exportEndpoint="ASSETS"
        filterParams={filterParams}
      />
    </div>
  );
}

const FilterContainer = styled.div`
  display: inline-flex;
  width: 44px;
  height: 44px;
  justify-content: center;
  align-items: center;
`;

const EmptyTableContainer = styled.div`
  background-color: #f4f7fb;
  width: 100%;
  height: 100%;
  text-align: center;
  font-weight: 400;
  font-size: 13px;
  justify-content: center;
`;
EmptyTableContainer.displayName = 'EmptyTableContainer';

const ClearFilterText = styled.div`
  padding-bottom: 8px;
`;

const ClearFilterButton = styled.button`
  background: none;
  border: none;
  text-decoration: underline;
`;

const InfoContainer = styled.div`
  padding-top: 33vh;
`;

const InfoEmptyContainer = styled.div`
  padding-top: 40vh;
`;

export default AssetsReportTemplateReport;
